import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import TextField from "@/common/components/TextField";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronsRight } from "lucide-react";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link, useParams } from "react-router-dom";
import GoBackButton from "@/common/components/GoBackButton";
import { FormTransformers } from "@/common/utils/formTransformers";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { useGoBack } from "@/common/hooks/useGoBack";
import { useQueryClient } from "@tanstack/react-query";
import TextArea from "@/common/components/TextArea";
import { DateInput } from "@/common/components/DateInput/DateInput";
import Checkbox from "@/common/components/Checkbox";
import SelectController from "@/common/components/SelectController";
import useHoliday from "../../hooks/useHoliday";
import useUpdateHoliday from "../../hooks/useUpdateHoliday";
import { createSchema, HolidayValidationSchema } from "../../schemas/create.schema";
import { FilterOptionOption } from "react-select";
import { filterOptions } from "@/features/users/constants/filterOptions.constant";
import { GeoTransformers } from "@/common/services/geoTransformers";
import { useGeosOptions } from "@/common/hooks/queries/useGeosOptions";
import useDebounce from "@/common/hooks/useDebounce";
import { formatDateInLocalTimezone } from "@/common/utils/formatters";

const Update = () => {
  const { id } = useParams();

  const goBack = useGoBack();
  const [messages, setMessages] = useState<string[] | boolean>(false)
  const queryClient = useQueryClient();

  const [geoSearch, setGeoSearch] = useDebounce('')

  const geos = useGeosOptions(geoSearch)

  const {
    data: initialHoliday,
    isLoading
  } = useHoliday(id || '');

  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm<HolidayValidationSchema>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      display_name: initialHoliday?.display_name,
      description: initialHoliday?.description,
      blocks_establishment_payment_advances: initialHoliday?.blocks_establishment_payment_advances,
      date: initialHoliday?.date && formatDateInLocalTimezone(initialHoliday?.date),
      geo: {
        value: initialHoliday?.geo.id,
        label: initialHoliday?.geo.display_name
      }
    }
  });

  const {
    mutate,
    isPending
  } = useUpdateHoliday({
    id: id || '',
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['holiday', id] });
      goBack({ fallback: `/holidays/${id}` })
    },
    onError: (error) => {
      if (error?.response?.data?.errors.base) {
        setMessages(error.response?.data.errors.base)
      } else {
        FormTransformers.errorsTransformer({ setError, setMessages })(error)
      }
    }
  })

  const onSubmit: SubmitHandler<HolidayValidationSchema> = (data) => mutate(data);

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="false">
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-2 items-center">
          <GoBackButton fallback={`/holidays/${id}`} />

          <p className="text-white-400 font-normal">
            Feriados
          </p>
          <ChevronsRight color='#ffffffb3' />

          <h2>
            Editar feriado
          </h2>
        </div>

        <div className="flex gap-4">
          <Link to={`/holidays/${id}`}>
            <Button variant='outline-error'>
              Cancelar
            </Button>
          </Link>

          <Button data-testid='update_holiday' type="submit" className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]" isLoading={isPending}>
            Confirmar
          </Button>
        </div>
      </div>

      {isLoading ? (
        <Box>Carregando...</Box>
      ) : (
        <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
          <p className="text-lg font-bold">
            Editar feriado
          </p>

          <div className="flex gap-2">
            <TextField
              label="Nome do feriado"
              markAsRequired
              errorMessage={errors?.display_name?.message}
              {...register('display_name')}
              className="w-full"
            />
            <DateInput
              fieldName="date"
              control={control}
              errors={errors}
              label="Data"
              markAsRequired
            />
          </div>

          <TextArea
            label="Descrição"
            markAsRequired
            errorMessage={errors?.description?.message}
            {...register('description')}
            maxLength={512}
          />

          <Checkbox
            label="Bloquear adiantamento de fatura para essa data"
            errorMessage={errors?.blocks_establishment_payment_advances?.message || undefined}
            {...register('blocks_establishment_payment_advances')}
          />

          <SelectController
            control={control}
            name="geo"
            label="Aplicável na região"
            filterOption={
              filterOptions as unknown as (option: FilterOptionOption<unknown>, inputValue: string) => boolean
            }
            defaultValue={GeoTransformers.mapToGeo()}
            options={GeoTransformers.mapToGeoOptions()(geos)}
            errorMessage={errors?.geo?.message || undefined}
            markAsRequired
            onInputChange={setGeoSearch}
          />
        </Box>
      )}

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </form>
  );
};

export default Update;
